import Loading from "@/components-legacy/loading";
import {
  useCreatePersonalAccessToken,
  usePersonalAccessTokens,
  useRevokePersonalAccessToken,
} from "@babackapp/private-api-client/client/use-personal-access-tokens";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { Input } from "@babackapp/ui/components/input";
import { ClipboardCheck, ClipboardCopy, Trash2 } from "lucide-react";
import { useState } from "react";

interface AccessTokensCardProps {
  onTokenCreated: (token: string) => void;
}

export default function AccessTokensCard({
  onTokenCreated,
}: AccessTokensCardProps) {
  const [label, setLabel] = useState("");
  const { data: tokens, isLoading } = usePersonalAccessTokens();
  const { mutateAsync: createToken, isPending: isCreating } =
    useCreatePersonalAccessToken();
  const { mutateAsync: revokeToken, isPending: isRevoking } =
    useRevokePersonalAccessToken();

  const handleCreate = async () => {
    if (!label.trim()) return;
    const created = await createToken({ label: label.trim() });
    setLabel("");
    onTokenCreated(created.token);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Personal access tokens</CardTitle>
        <CardDescription>
          A token identifies you, not a shop: it reaches every shop you own or
          administer. The value is shown once, at creation.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex gap-2">
          <Input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Token name, e.g. Claude Code"
            onKeyDown={(event) => {
              if (event.key === "Enter") void handleCreate();
            }}
          />
          <Button
            onClick={() => void handleCreate()}
            disabled={isCreating || !label.trim()}
          >
            {isCreating ? <Loading className="w-4 h-4" /> : "Create"}
          </Button>
        </div>

        {isLoading ? <Loading className="w-4 h-4" /> : null}

        {tokens?.length ? (
          <div className="flex flex-col divide-y border rounded">
            {tokens.map((token) => (
              <div
                key={token.id}
                className="flex items-center justify-between gap-4 px-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{token.label}</span>
                  <span className="text-xs text-muted-foreground font-mono">
                    pat_…{token.tokenHint}
                    {token.lastUsedAt
                      ? ` · last used ${new Date(token.lastUsedAt).toLocaleString()}`
                      : " · never used"}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isRevoking}
                  onClick={() => void revokeToken(token.id)}
                >
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          !isLoading && (
            <p className="text-sm text-muted-foreground">No token yet.</p>
          )
        )}
      </CardContent>
    </Card>
  );
}

export function CreatedTokenBanner({ token }: { token: string }) {
  const [hasCopied, setHasCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(token);
    setHasCopied(true);
    setTimeout(() => setHasCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-2 rounded border border-green-500/40 bg-green-500/5 p-3">
      <span className="text-xs text-muted-foreground">
        Copy it now, it will not be shown again.
      </span>
      <button
        type="button"
        onClick={handleCopy}
        className="flex items-center justify-between gap-2 rounded border bg-muted px-3 py-2 text-left font-mono text-sm hover:bg-muted/70 transition-colors"
      >
        <span className="break-all">{token}</span>
        {hasCopied ? (
          <ClipboardCheck className="size-4 shrink-0 text-green-500" />
        ) : (
          <ClipboardCopy className="size-4 shrink-0 text-muted-foreground" />
        )}
      </button>
    </div>
  );
}
